import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { useCart } from '../hooks/useCart';
import StripeCheckoutForm from '../components/StripeCheckoutForm';
import ConfirmationModal from '../components/ConfirmationModal';
import Spinner from '../components/Spinner';
import './CheckoutPage.css';

// Load Stripe outside component to avoid recreating on every render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();

  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    shippingAddress: '',
    shippingCity: '',
    shippingState: '',
    shippingZip: '',
    shippingCountry: 'US'
  });

  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);
  const [shippingInfo, setShippingInfo] = useState({
    freeShippingThreshold: 0,
    isFreeShipping: false,
    amountToFreeShipping: 0
  });
  const [orderSuccess, setOrderSuccess] = useState(null);

  useEffect(() => {
    // Calculate shipping when cart changes
    if (cart.length > 0) {
      calculateShipping();
    }
  }, [cart]);

  useEffect(() => {
    // Create payment intent when shipping cost is calculated
    if (cart.length > 0 && shippingCost >= 0) {
      createPaymentIntent();
    }
  }, [shippingCost]);

  const calculateShipping = async () => {
    try {
      const subtotal = getCartTotal();
      const items = cart.map(item => ({
        product_id: item.product.id,
        variation_id: item.variation?.id,
        quantity: item.quantity,
        price: item.variation?.price || item.product.price
      }));

      const response = await axios.post(`${API_URL}/api/payment/calculate-shipping`, {
        items,
        shippingCountry: formData.shippingCountry,
        subtotal
      });

      setShippingCost(response.data.shippingCost);
      setShippingInfo({
        freeShippingThreshold: response.data.freeShippingThreshold,
        isFreeShipping: response.data.isFreeShipping,
        amountToFreeShipping: response.data.amountToFreeShipping
      });
    } catch (err) {
      console.error('Error calculating shipping:', err);
      setShippingCost(0);
    }
  };

  const createPaymentIntent = async () => {
    try {
      setLoading(true);
      const totalAmount = getCartTotal() + shippingCost;
      const response = await axios.post(`${API_URL}/api/payment/create-payment-intent`, {
        amount: totalAmount,
        currency: 'usd',
        metadata: {
          cart_items: cart.length,
          shipping_cost: shippingCost
        }
      });

      setClientSecret(response.data.clientSecret);
      setLoading(false);
    } catch (err) {
      console.error('Error creating payment intent:', err);
      setError('Failed to initialize checkout. Please try again.');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePaymentSuccess = (result) => {
    // Clear cart
    clearCart();

    // Show success modal
    setOrderSuccess(result.orderNumber);
  };

  const handleCloseSuccessModal = () => {
    setOrderSuccess(null);
    navigate('/');
  };

  const handlePaymentError = (error) => {
    setError(error.message);
  };

  if (cart.length === 0) {
    return (
      <div className="empty-checkout">
        <h2>Your cart is empty</h2>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Continue Shopping
        </button>
      </div>
    );
  }

  // Prepare order data
  const orderData = {
    customerName: formData.customerName,
    customerEmail: formData.customerEmail,
    customerPhone: formData.customerPhone,
    shippingAddress: formData.shippingAddress,
    shippingCity: formData.shippingCity,
    shippingState: formData.shippingState,
    shippingZip: formData.shippingZip,
    shippingCountry: formData.shippingCountry,
    items: cart.map(item => ({
      product_id: item.product.id,
      variation_id: item.variation?.id,
      quantity: item.quantity,
      price: item.variation?.price || item.product.price
    })),
    total: getCartTotal() + shippingCost
  };

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#e33d3d',
      colorBackground: '#ffffff',
      colorText: '#333333',
      colorDanger: '#f44336',
      fontFamily: '"Rubik", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      spacingUnit: '4px',
      borderRadius: '0px',
    },
  };

  const options = {
    clientSecret,
    appearance,
  };

  return (
    <div className="checkout-page">
      <ConfirmationModal
        isOpen={!!orderSuccess}
        onClose={handleCloseSuccessModal}
        title="Order Placed Successfully!"
        actions={
          <button className="btn btn-primary" onClick={handleCloseSuccessModal}>
            Continue Shopping
          </button>
        }
      >
        <p>Thank you for your order!</p>
        <p><strong>Order Number: {orderSuccess}</strong></p>
        <p>We've sent a confirmation email with your order details.</p>
      </ConfirmationModal>

      <h1>Checkout</h1>

      <div className="checkout-container">
        <div className="checkout-form-wrapper">
          <section className="form-section">
            <h2>Contact Information</h2>

            <div className="form-group">
              <label htmlFor="customerName">Full Name *</label>
              <input
                type="text"
                id="customerName"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="customerEmail">Email *</label>
                <input
                  type="email"
                  id="customerEmail"
                  name="customerEmail"
                  value={formData.customerEmail}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="customerPhone">Phone *</label>
                <input
                  type="tel"
                  id="customerPhone"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2>Shipping Address</h2>

            <div className="form-group">
              <label htmlFor="shippingAddress">Street Address *</label>
              <input
                type="text"
                id="shippingAddress"
                name="shippingAddress"
                value={formData.shippingAddress}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="shippingCity">City *</label>
                <input
                  type="text"
                  id="shippingCity"
                  name="shippingCity"
                  value={formData.shippingCity}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="shippingState">State *</label>
                <input
                  type="text"
                  id="shippingState"
                  name="shippingState"
                  value={formData.shippingState}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="shippingZip">ZIP Code *</label>
                <input
                  type="text"
                  id="shippingZip"
                  name="shippingZip"
                  value={formData.shippingZip}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <input type="hidden" name="shippingCountry" value="US" />
          </section>

          <section className="form-section">
            <h2>Payment Information</h2>

            {error && <div className="error-message">{error}</div>}

            {loading && <Spinner size="small" text="Initializing payment..." />}

            {clientSecret && !loading && (
              <Elements stripe={stripePromise} options={options}>
                <StripeCheckoutForm
                  clientSecret={clientSecret}
                  orderData={orderData}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                />
              </Elements>
            )}
          </section>
        </div>

        <div className="order-summary">
          <h2>Order Summary</h2>

          <div className="summary-items">
            {cart.map((item, index) => {
              const price = item.variation?.price || item.product.price;
              const itemTotal = price * item.quantity;

              return (
                <div key={`${item.product.id}-${item.variation?.id || 'no-var'}-${index}`} className="summary-item">
                  <div className="item-info">
                    <div className="item-name">{item.product.name}</div>
                    {item.variation && (
                      <div className="item-variation">
                        {item.variation.color} • {item.variation.size}
                      </div>
                    )}
                    <div className="item-quantity">Qty: {item.quantity}</div>
                  </div>
                  <div className="item-total">${itemTotal.toFixed(2)}</div>
                </div>
              );
            })}
          </div>

          <div className="summary-totals">
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>${getCartTotal().toFixed(2)}</span>
            </div>

            <div className="summary-row">
              <span>Shipping:</span>
              <span>
                {shippingInfo.isFreeShipping ? (
                  <span className="free-shipping-badge">FREE</span>
                ) : (
                  `$${shippingCost.toFixed(2)}`
                )}
              </span>
            </div>

            {!shippingInfo.isFreeShipping && shippingInfo.amountToFreeShipping > 0 && (
              <div className="free-shipping-progress">
                Add ${shippingInfo.amountToFreeShipping.toFixed(2)} more for free shipping!
              </div>
            )}

            <div className="summary-row total">
              <span>Total:</span>
              <span>${(getCartTotal() + shippingCost).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
